/**
 * Granular shallow selectors for PvP room UI components.
 * Each selector subscribes to a minimal slice of viewState to avoid unnecessary re-renders.
 */

import { useEffect, useMemo, useState } from "react"
import { usePvpRouletteRuntimeRevision } from "@/shared/lib/pvp-roulette-runtime/useRuntimeRevision"
import { getPvpRouletteRuntime } from "@/shared/lib/pvp-roulette-runtime/runtime"
import { useShallow } from "zustand/react/shallow"
import { deriveViewState, usePvpRoomsStore } from "@/entities/pvp-roulette"
import { getUserIdFromToken } from "@/shared/api/auth"
import { useLiveRemainingMs } from "./useLiveRemainingMs"
import {
  getActiveBets,
  normalizeUnixTimestampToMs,
} from "@/shared/api/types/pvpRoulette"
import type {
  ActivityFeedItem,
  ViewPhase,
  RoundParty,
  BanksState,
  BetRestrictions,
  SnapshotBet,
} from "@/entities/pvp-roulette"

function isTeamSectorParty(p: RoundParty): boolean {
  return p.partyType === "TEAM"
}

/**
 * Resolves `winnerPartyId` from the round snapshot to a `RoundParty`.
 * Uses `winnerPartyType` for SOLO vs TEAM — not string shape of `winnerPartyId`.
 */
function findPartyByWinnerId(
  parties: RoundParty[],
  winnerPartyId: string,
  winnerPartyType?: "SOLO" | "TEAM"
): RoundParty | null {
  const direct = parties.find(
    (p) =>
      p.partyId === winnerPartyId ||
      p.userId === winnerPartyId ||
      (p.id !== undefined && p.id === winnerPartyId)
  )
  if (direct) return direct
  if (winnerPartyType === "TEAM") return null

  return (
    parties.find(
      (p) =>
        !isTeamSectorParty(p) &&
        (p.partyId === winnerPartyId ||
          p.userId === winnerPartyId ||
          (p.id !== undefined && p.id === winnerPartyId) ||
          `solo:${p.userId}` === winnerPartyId)
    ) ?? null
  )
}

export function usePvpRoomPhase(roomId: string | null): ViewPhase | null {
  return usePvpRoomsStore((s) =>
    roomId ? (s.rooms[roomId]?.viewState?.phase ?? null) : null
  )
}

/**
 * Like `usePvpRoomPhase` but optimistically advances SPINNING → TRICKS once
 * `spinningEndsAt` has elapsed on the client clock.
 *
 * This ensures the TRICKS progress ring starts at 0%: the server sets
 * `tricksStartedAt = spinningEndsAt` during SPINNING, so by the time we
 * optimistically switch the phase the denominator (tricksDuration) and the
 * numerator (liveTricks) are both measured from the same instant.
 *
 * Deduplication is free: when the real TRICKS snapshot arrives it sets
 * `snapshot.status = 'TRICKS'` and this hook returns 'TRICKS' from the store
 * directly — no double-transition, no extra effect.
 */
export function useLivePvpRoomPhase(
  roomId: string | null,
  opts?: { nowMs?: number | null }
): ViewPhase | null {
  const phase = usePvpRoomPhase(roomId)
  const timers = usePvpRoomTimers(roomId)
  const liveSpinning = useLiveRemainingMs(
    phase === "SPINNING" ? (timers?.endsAtSpinningMs ?? null) : null,
    phase === "SPINNING",
    { nowMs: opts?.nowMs }
  )
  if (phase === "SPINNING" && liveSpinning === 0) return "TRICKS"
  return phase
}

export function usePvpRoomRoundInfo(roomId: string | null) {
  return usePvpRoomsStore(
    useShallow((s) => {
      if (!roomId) return null
      const vs = s.rooms[roomId]?.viewState
      if (!vs) return null
      return { roundId: vs.roundId, roundNum: vs.roundNum, status: vs.status }
    })
  )
}

export function usePvpRoomTimers(roomId: string | null) {
  return usePvpRoomsStore(
    useShallow((s) => {
      if (!roomId) return null
      const vs = s.rooms[roomId]?.viewState
      if (!vs) return null
      return {
        remainingMsBetting: vs.remainingMsBetting,
        remainingMsSpinning: vs.remainingMsSpinning,
        remainingMsTricks: vs.remainingMsTricks,
        remainingMsSlowing: vs.remainingMsSlowing,
        endsAtBettingMs: vs.endsAtBettingMs,
        endsAtSpinningMs: vs.endsAtSpinningMs,
        endsAtTricksMs: vs.endsAtTricksMs,
        endsAtSlowingMs: vs.endsAtSlowingMs,
      }
    })
  )
}

export function usePvpRoomPhaseDurations(roomId: string | null) {
  const rtRev = usePvpRouletteRuntimeRevision()
  const rawRound = usePvpRoomsStore((s) =>
    roomId ? s.rooms[roomId]?.rawRound : null
  )
  return useMemo(() => {
    const r = rawRound
    if (!r) return null

    const bettingStartMs =
      r.bettingStartedAt != null && Number.isFinite(r.bettingStartedAt)
        ? normalizeUnixTimestampToMs(r.bettingStartedAt)
        : null
    const bettingEndMs =
      r.bettingEndsAt != null && Number.isFinite(r.bettingEndsAt)
        ? normalizeUnixTimestampToMs(r.bettingEndsAt)
        : null
    const bettingDurationMs =
      bettingStartMs != null &&
      bettingEndMs != null &&
      bettingEndMs > bettingStartMs
        ? bettingEndMs - bettingStartMs
        : typeof r.bettingDurationSeconds === "number" &&
            r.bettingDurationSeconds > 0
          ? r.bettingDurationSeconds * 1000
          : null

    const tricksVisualLeadMs = getPvpRouletteRuntime().TRICKS_VISUAL_END_LEAD_MS
    const tricksStartRaw = r.tricksStartedAt ?? r.spinningEndsAt
    const tricksStartMs =
      tricksStartRaw != null && Number.isFinite(tricksStartRaw)
        ? normalizeUnixTimestampToMs(tricksStartRaw)
        : null
    let tricksVisualEndMs: number | null = null
    if (r.tricksEndsAt != null && Number.isFinite(r.tricksEndsAt)) {
      tricksVisualEndMs =
        normalizeUnixTimestampToMs(r.tricksEndsAt) - tricksVisualLeadMs
    } else if (
      tricksStartMs != null &&
      typeof r.tricksDurationSeconds === "number" &&
      r.tricksDurationSeconds > 0
    ) {
      tricksVisualEndMs =
        tricksStartMs + r.tricksDurationSeconds * 1000 - tricksVisualLeadMs
    }
    const tricksDurationMs =
      tricksStartMs != null &&
      tricksVisualEndMs != null &&
      tricksVisualEndMs > tricksStartMs
        ? tricksVisualEndMs - tricksStartMs
        : null

    return { bettingDurationMs, tricksDurationMs }
  }, [rawRound, rtRev])
}

export function usePvpRoomBanks(roomId: string | null): BanksState | null {
  return usePvpRoomsStore(
    useShallow((s) =>
      roomId ? (s.rooms[roomId]?.viewState?.banks ?? null) : null
    )
  )
}

export function usePvpRoomActionsAllowed(roomId: string | null) {
  return usePvpRoomsStore(
    useShallow((s) =>
      roomId ? (s.rooms[roomId]?.viewState?.actionsAllowed ?? null) : null
    )
  )
}

export function usePvpRoomParties(roomId: string | null): RoundParty[] {
  return usePvpRoomsStore(
    useShallow((s) =>
      roomId ? (s.rooms[roomId]?.viewState?.parties ?? []) : []
    )
  )
}

export function usePvpRoomBetRestrictions(
  roomId: string | null
): BetRestrictions | null {
  return usePvpRoomsStore(
    useShallow((s) =>
      roomId ? (s.rooms[roomId]?.viewState?.betRestrictions ?? null) : null
    )
  )
}

export function usePvpRoomWinner(roomId: string | null) {
  return usePvpRoomsStore(
    useShallow((s) => {
      if (!roomId) return null
      const r = s.rooms[roomId]?.rawRound
      const id = r?.winnerPartyId ?? null
      return id ? { partyId: id } : null
    })
  )
}

export function usePvpRoomResolvedWinnerMeta(roomId: string | null) {
  return usePvpRoomsStore(
    useShallow((s) => {
      if (!roomId) return null
      const r = s.rooms[roomId]?.rawRound
      const winnerPartyId = r?.winnerPartyId
      if (!r || !winnerPartyId) return null

      const winnerParty = findPartyByWinnerId(
        r.parties,
        winnerPartyId,
        r.winnerPartyType
      )

      const playerBanks = winnerParty?.playerBanks ?? []
      const winnerUsers = playerBanks
        .map((pb) => pb.userData)
        .filter((u) => !!u?.userId)
        .map((u) => ({
          userId: u.userId,
          username: u.username,
          photoUrl: u.photoUrl,
        }))

      return {
        winnerPartyId,
        winnerPartyType: r.winnerPartyType ?? winnerParty?.partyType ?? "SOLO",
        winnerParty,
        winnerUsers,
        prizePoolTon: r.prizePoolTon ?? "0",
        settlement: r.settlement ?? null,
        bets: r.bets ?? [],
      }
    })
  )
}

/** Server-derived activity feed (same data as former wheel bubbles; survives tab navigation). */
export function usePvpRoomActivityFeed(
  roomId: string | null
): ActivityFeedItem[] {
  return usePvpRoomsStore(
    useShallow((s) => (roomId ? (s.rooms[roomId]?.activityFeed ?? []) : []))
  )
}

/** All active/pending bets for the round (for per-user rows in the players list). */
export function usePvpRoomActiveBets(roomId: string | null): SnapshotBet[] {
  return usePvpRoomsStore(
    useShallow((s) => {
      if (!roomId) return []
      const r = s.rooms[roomId]?.rawRound
      return getActiveBets(r?.bets ?? [])
    })
  )
}

/** All bet IDs for the current user (parallel cancel). */
export function usePvpRoomMyBetIds(
  roomId: string | null,
  currentUserId: string | null
): string[] {
  return usePvpRoomsStore(
    useShallow((s) => {
      if (!roomId || !currentUserId) return []
      const r = s.rooms[roomId]?.rawRound
      const active = getActiveBets(r?.bets ?? [])
      return active
        .filter((b) => b.userData.userId === currentUserId)
        .map((b) => b.betId)
    })
  )
}

/** Type of the current user's existing bet: 'solo' | 'team' | null (no bet). Used to lock mode: can only add to same type. */
export function usePvpRoomMyBetType(
  roomId: string | null,
  currentUserId: string | null
): "solo" | "team" | null {
  return usePvpRoomsStore(
    useShallow((s) => {
      if (!roomId || !currentUserId) return null
      const r = s.rooms[roomId]?.rawRound
      const active = getActiveBets(r?.bets ?? [])
      const bet = active.find((b) => b.userData.userId === currentUserId)
      if (!bet) return null
      if (bet.partyType === "TEAM") {
        return "team"
      }
      return "solo"
    })
  )
}

/** Party ID of the current user's bet (solo:userId or Team 1, etc). */
export function usePvpRoomMyBetPartyId(
  roomId: string | null,
  currentUserId: string | null
): string | null {
  return usePvpRoomsStore(
    useShallow((s) => {
      if (!roomId || !currentUserId) return null
      const r = s.rooms[roomId]?.rawRound
      const active = getActiveBets(r?.bets ?? [])
      const bet = active.find((b) => b.userData.userId === currentUserId)
      return bet?.partyId ?? (bet ? `solo:${currentUserId}` : null)
    })
  )
}

/** Shared rAF (~60 Hz) for hub-derived timers (BETTING + TRICKS); one loop for all subscribers. */
const pvpHubUiFrameListeners = new Set<() => void>()
let pvpHubUiFrameRaf = 0

function notifyPvpHubUiFrameListeners() {
  pvpHubUiFrameListeners.forEach((fn) => {
    fn()
  })
}

function pvpHubUiFrameStep() {
  notifyPvpHubUiFrameListeners()
  if (pvpHubUiFrameListeners.size > 0) {
    pvpHubUiFrameRaf = requestAnimationFrame(pvpHubUiFrameStep)
  } else {
    pvpHubUiFrameRaf = 0
  }
}

function subscribePvpHubUiFrame(listener: () => void): () => void {
  pvpHubUiFrameListeners.add(listener)
  if (pvpHubUiFrameListeners.size === 1) {
    pvpHubUiFrameRaf = requestAnimationFrame(pvpHubUiFrameStep)
  }
  return () => {
    pvpHubUiFrameListeners.delete(listener)
    if (pvpHubUiFrameListeners.size === 0 && pvpHubUiFrameRaf !== 0) {
      cancelAnimationFrame(pvpHubUiFrameRaf)
      pvpHubUiFrameRaf = 0
    }
  }
}

export interface LivePvpHubUi {
  remainingMsBetting: number | null
  remainingMsTricks: number | null
  endsAtTricksMs: number | null
  canApplyTrickByType: Record<string, boolean>
}

export type LivePvpHubUiScope = "full" | "tricksOnly"

/**
 * Re-runs `deriveViewState` on the wall clock while the hub shows a **live** countdown ring
 * (BETTING or TRICKS per `useLivePvpRoomPhase`). One shared rAF for all subscribers; same
 * `remainingMsTricks` as `canApplyTrickByType` (do not mix in a separate `useLiveRemainingMs`).
 *
 * `hubScope: 'tricksOnly'` — subscribe only in TRICKS (e.g. `TricksPanel`); avoids rAF+derive during BETTING.
 */
export function useLivePvpHubUi(
  roomId: string | null,
  opts?: { nowMs?: number | null; hubScope?: LivePvpHubUiScope }
): LivePvpHubUi {
  const rtRev = usePvpRouletteRuntimeRevision()
  const rawRound = usePvpRoomsStore((s) =>
    roomId ? s.rooms[roomId]?.rawRound : null
  )
  const clockOffsetMs = usePvpRoomsStore((s) =>
    roomId ? s.rooms[roomId]?.clockOffsetMs : undefined
  )
  const phase = useLivePvpRoomPhase(roomId, { nowMs: opts?.nowMs })
  const externalNow = opts?.nowMs
  const hasFixedNow = externalNow != null && Number.isFinite(externalNow)
  const scope = opts?.hubScope ?? "full"

  const [renderGen, setRenderGen] = useState(0)
  const hubNeedsLiveClock =
    phase === "TRICKS" || (scope === "full" && phase === "BETTING")
  const shouldTick =
    !hasFixedNow && roomId != null && rawRound != null && hubNeedsLiveClock

  useEffect(() => {
    if (!shouldTick) return
    return subscribePvpHubUiFrame(() => setRenderGen((n) => n + 1))
  }, [shouldTick, roomId, rawRound?.version, rawRound?.roundId])

  return useMemo(() => {
    if (!rawRound) {
      return {
        remainingMsBetting: null,
        remainingMsTricks: null,
        endsAtTricksMs: null,
        canApplyTrickByType: {} as Record<string, boolean>,
      }
    }
    const now = hasFixedNow ? (externalNow as number) : Date.now()
    const vs = deriveViewState(rawRound, {
      now,
      userId: getUserIdFromToken() ?? undefined,
      clockOffsetMs,
    })
    return {
      remainingMsBetting: vs.remainingMsBetting,
      remainingMsTricks: vs.remainingMsTricks,
      endsAtTricksMs: vs.endsAtTricksMs,
      canApplyTrickByType: vs.actionsAllowed.canApplyTrickByType,
    }
  }, [
    rawRound,
    externalNow,
    hasFixedNow,
    phase,
    shouldTick,
    renderGen,
    scope,
    rtRev,
    clockOffsetMs,
  ])
}

export function usePvpRoomFairnessHashes(roomId: string | null): {
  clientCommitHash: string
  saltHash: string
} {
  return usePvpRoomsStore(
    useShallow((s) => {
      const r = roomId ? s.rooms[roomId]?.rawRound : null
      return {
        clientCommitHash: r?.clientCommitHash ?? "",
        saltHash: r?.saltHash ?? "",
      }
    })
  )
}
